import { useEffect, useState, FC, ReactNode, CSSProperties } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Grid,
  Tooltip,
  InputAdornment
} from "@mui/material";
import {
  Groups,
  Person,
  ContactPhone,
  Email,
  SwapHoriz,
  Inventory,
  Repeat,
  Language,
  BarChart,
  Comment,
  Bolt,
  AccountBalance,
  Hub,
  Storefront
} from "@mui/icons-material";
import { MFT_ENERGY_AS, mftCompanies } from "../../../utils/consts";
import { CreateCountryRequest, VpDto } from "../../../api/Api";
import { VpPicker } from "../../../components/vpPicker";
import { UserNamePicker } from "../../../components/userPicker";
import { CountrySelect } from "../../../components/countrySelect";
import { buildCommentDto } from "../../../utils/commentUtils";
import { US, DK, JP, TR, EU, GB } from "country-flag-icons/react/3x2";
import { useThemeMode } from "../../../theme";

type Props = {
  initialValues?: CreateCountryRequest | null;
  onSubmit: (payload: CreateCountryRequest) => void;
  submitting?: boolean;
  submitDisabled?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
  cancelLabel?: string;
  secondaryAction?: ReactNode;
};

type FlagProps = { height: number; title?: string; style?: CSSProperties };

const UNIT_OPTIONS: { value: string; Flag?: FC<FlagProps> }[] = [
  { value: "DKK", Flag: DK },
  { value: "EUR", Flag: EU },
  { value: "GBP", Flag: GB },
  { value: "JPY", Flag: JP },
  { value: "TRY", Flag: TR },
  { value: "USD", Flag: US },
  { value: "MW" }
];

/*
 * Country addition request form.
 *
 * The field list comes from board comments #45-#51 rather than from the counterparty form it was drawn over:
 *   #45 contracting entity is not shown here at all, it just defaults to MFT Energy A/S
 *   #46 TSO (if applicable), revealing contact name + email once filled
 *   #47 Exchange (if applicable), same reveal
 *   #48/#49 clearing bank and its contact name + email
 *   #50 no agreement type
 *   #51 Business Rational replaces "Other info or comments" as free text
 */
export const CountryForm: FC<Props> = ({
  initialValues,
  onSubmit,
  submitting = false,
  submitDisabled: submitDisabledProp = false,
  submitLabel = "Submit",
  onCancel,
  cancelLabel = "Cancel",
  secondaryAction
}) => {
  const [tradingTeam, setTradingTeam] = useState<string>("");
  const [contactPersonTradingTeam, setContactPersonTradingTeam] =
    useState<string>("");
  const [countryName, setCountryName] = useState<string>("");
  const [tsoName, setTsoName] = useState<string>("");
  const [tsoContactName, setTsoContactName] = useState<string>("");
  const [tsoContactEmail, setTsoContactEmail] = useState<string>("");
  const [exchangeName, setExchangeName] = useState<string>("");
  const [exchangeContactName, setExchangeContactName] = useState<string>("");
  const [exchangeContactEmail, setExchangeContactEmail] = useState<string>("");
  const [clearingBankName, setClearingBankName] = useState<string>("");
  const [clearingBankContactName, setClearingBankContactName] =
    useState<string>("");
  const [clearingBankContactEmail, setClearingBankContactEmail] =
    useState<string>("");
  const [typeOfTrading, setTypeOfTrading] = useState<string>("");
  const [productType, setProductType] = useState<string>("");
  const [venue, setVenue] = useState<string>("");
  const [transactionType, setTransactionType] = useState<string>("");
  const [unit, setUnit] = useState<string>("");
  const [volumeSize, setVolumeSize] = useState<number | undefined>(undefined);
  const [mandate, setMandate] = useState<boolean>(false);
  const [urgent, setUrgent] = useState<boolean>(false);
  const [businessRational, setBusinessRational] = useState<string>("");

  const [vp, setVp] = useState<VpDto | null>(null);

  const { mode } = useThemeMode();

  useEffect(() => {
    setTradingTeam(initialValues?.tradingTeam ?? "");
    setContactPersonTradingTeam(initialValues?.contactPersonTradingTeam ?? "");
    setCountryName(initialValues?.countryName ?? "");
    setTsoName(initialValues?.tsoName ?? "");
    setTsoContactName(initialValues?.tsoContactName ?? "");
    setTsoContactEmail(initialValues?.tsoContactEmail ?? "");
    setExchangeName(initialValues?.exchangeName ?? "");
    setExchangeContactName(initialValues?.exchangeContactName ?? "");
    setExchangeContactEmail(initialValues?.exchangeContactEmail ?? "");
    setClearingBankName(initialValues?.clearingBankName ?? "");
    setClearingBankContactName(initialValues?.clearingBankContactName ?? "");
    setClearingBankContactEmail(initialValues?.clearingBankContactEmail ?? "");
    setTypeOfTrading(initialValues?.typeOfTrading ?? "");
    setProductType(initialValues?.productType ?? "");
    setVenue(initialValues?.venue ?? "");
    setTransactionType(initialValues?.transactionType ?? "");
    setUnit(initialValues?.unit ?? "");
    setVolumeSize(initialValues?.volumeSize);
    setMandate(initialValues?.mandate ?? false);
    setUrgent(initialValues?.urgent ?? false);
    setBusinessRational(initialValues?.businessRational ?? "");
    setVp(initialValues?.vp ?? null);
  }, [initialValues]);

  const buildFormData = (): CreateCountryRequest => {
    const trimmedRational = businessRational.trim();

    return {
      tradingTeam: tradingTeam.trim() || undefined,
      contactPersonTradingTeam: contactPersonTradingTeam.trim() || undefined,
      countryName: countryName.trim() || undefined,
      tsoName: tsoName.trim() || undefined,
      tsoContactName: tsoContactName.trim() || undefined,
      tsoContactEmail: tsoContactEmail.trim() || undefined,
      exchangeName: exchangeName.trim() || undefined,
      exchangeContactName: exchangeContactName.trim() || undefined,
      exchangeContactEmail: exchangeContactEmail.trim() || undefined,
      clearingBankName: clearingBankName.trim() || undefined,
      clearingBankContactName: clearingBankContactName.trim() || undefined,
      clearingBankContactEmail: clearingBankContactEmail.trim() || undefined,
      typeOfTrading: typeOfTrading.trim() || undefined,
      productType: productType.trim() || undefined,
      venue: venue.trim() || undefined,
      transactionType: transactionType.trim() || undefined,
      unit: unit.trim() || undefined,
      volumeSize: volumeSize,
      // #45: never shown on this form, but still stored so the rest of the app can rely on it.
      contractingEntity: initialValues?.contractingEntity ?? MFT_ENERGY_AS,
      businessRational: trimmedRational || undefined,
      vp: vp ?? undefined,
      mandate,
      urgent,
      // The business rational doubles as the opening comment on the country, same as the other books.
      comment: trimmedRational.length
        ? buildCommentDto(trimmedRational)
        : undefined
    };
  };

  const submitDisabled =
    !tradingTeam ||
    !contactPersonTradingTeam ||
    !countryName ||
    !typeOfTrading ||
    !productType ||
    !venue.trim() ||
    !transactionType ||
    !vp?.objectId;

  const adornment = (Icon: typeof Person) => ({
    input: {
      startAdornment: (
        <InputAdornment position="start">
          <Icon fontSize="small" />
        </InputAdornment>
      )
    }
  });

  return (
    <Grid container spacing={2} sx={{ paddingTop: 2 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FormControl fullWidth required>
            <InputLabel>Trading team</InputLabel>
            <Select
              value={tradingTeam}
              onChange={(e) => setTradingTeam(e.target.value)}
              label="Trading team"
              startAdornment={
                <InputAdornment position="start">
                  <Groups fontSize="small" />
                </InputAdornment>
              }
            >
              {mftCompanies.map((company) => {
                const split = company.split("MFT");
                return (
                  <MenuItem key={company} value={company}>
                    {"MFT " + split[1]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <UserNamePicker
            label="Contact person from MFT trading team"
            value={contactPersonTradingTeam}
            onChange={setContactPersonTradingTeam}
            required
            startIcon={<Person fontSize="small" />}
          />

          <VpPicker value={vp} onChange={setVp} required />

          <CountrySelect
            label="Country"
            value={countryName}
            onChange={setCountryName}
            required
          />

          <TextField
            label="TSO (if applicable)"
            value={tsoName}
            onChange={(e) => setTsoName(e.target.value)}
            fullWidth
            slotProps={adornment(Hub)}
          />

          {/* #46 reply: the contact pair only matters once a TSO has actually been named. */}
          {tsoName.trim().length > 0 && (
            <>
              <TextField
                label="TSO contact name"
                value={tsoContactName}
                onChange={(e) => setTsoContactName(e.target.value)}
                fullWidth
                slotProps={adornment(ContactPhone)}
              />
              <TextField
                label="TSO contact email"
                value={tsoContactEmail}
                onChange={(e) => setTsoContactEmail(e.target.value)}
                fullWidth
                slotProps={adornment(Email)}
              />
            </>
          )}

          <TextField
            label="Exchange (if applicable)"
            value={exchangeName}
            onChange={(e) => setExchangeName(e.target.value)}
            fullWidth
            slotProps={adornment(Storefront)}
          />

          {/* #47 reply: same reveal as the TSO pair. */}
          {exchangeName.trim().length > 0 && (
            <>
              <TextField
                label="Exchange contact name"
                value={exchangeContactName}
                onChange={(e) => setExchangeContactName(e.target.value)}
                fullWidth
                slotProps={adornment(ContactPhone)}
              />
              <TextField
                label="Exchange contact email"
                value={exchangeContactEmail}
                onChange={(e) => setExchangeContactEmail(e.target.value)}
                fullWidth
                slotProps={adornment(Email)}
              />
            </>
          )}

          <TextField
            label="Clearing bank (if applicable)"
            value={clearingBankName}
            onChange={(e) => setClearingBankName(e.target.value)}
            fullWidth
            slotProps={adornment(AccountBalance)}
          />

          {clearingBankName.trim().length > 0 && (
            <>
              <TextField
                label="Clearing bank contact name (if applicable)"
                value={clearingBankContactName}
                onChange={(e) => setClearingBankContactName(e.target.value)}
                fullWidth
                slotProps={adornment(ContactPhone)}
              />

              <TextField
                label="Clearing bank contact email (if applicable)"
                value={clearingBankContactEmail}
                onChange={(e) => setClearingBankContactEmail(e.target.value)}
                fullWidth
                slotProps={adornment(Email)}
              />
            </>
          )}
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TextField
            label="Type of trading/settlement"
            value={typeOfTrading}
            onChange={(e) => setTypeOfTrading(e.target.value)}
            fullWidth
            required
            slotProps={adornment(SwapHoriz)}
          />

          <TextField
            label="Product type"
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            fullWidth
            required
            slotProps={adornment(Inventory)}
          />

          <TextField
            label="Venue"
            value={venue}
            placeholder="Nasdaq, EEX, Trayport, ICE etc."
            onChange={(e) => setVenue(e.target.value)}
            fullWidth
            required
            slotProps={adornment(Language)}
          />

          <FormControl fullWidth required>
            <InputLabel>Transaction type</InputLabel>
            <Select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              label="Transaction type"
              startAdornment={
                <InputAdornment position="start">
                  <Repeat fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="one-off">One-off Transaction</MenuItem>
              <MenuItem value="recurring">Recurring Transactions</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Deal Unit</InputLabel>
            <Select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              label="Deal Unit"
              startAdornment={
                <InputAdornment position="start">
                  <Language fontSize="small" />
                </InputAdornment>
              }
            >
              {UNIT_OPTIONS.map(({ value, Flag }) => (
                <MenuItem key={value} value={value}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    justifyContent="space-between"
                    width="64px"
                  >
                    <Typography variant="body1">{value}</Typography>
                    {Flag ? (
                      <Flag
                        height={12}
                        style={{
                          border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                        }}
                      />
                    ) : (
                      <Bolt height={12} color="warning" />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Expected deal volume/size"
            type="number"
            value={volumeSize || ""}
            onChange={(e) =>
              setVolumeSize(e.target.value ? Number(e.target.value) : undefined)
            }
            fullWidth
            slotProps={adornment(BarChart)}
          />

          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={mandate}
                onChange={(e) => setMandate(e.target.checked)}
              />
            }
            label="Mandate granted"
          />

          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={urgent}
                onChange={(e) => setUrgent(e.target.checked)}
              />
            }
            label="Mark as urgent"
          />

          {/* #51: open text, replaces the counterparty form's "Other info or comments". */}
          <TextField
            label="Business Rational"
            value={businessRational}
            placeholder="Why this country addition is needed"
            onChange={(e) => setBusinessRational(e.target.value)}
            fullWidth
            multiline
            rows={4}
            slotProps={adornment(Comment)}
          />
        </Box>
      </Grid>

      <Grid size={12}>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          paddingTop={1}
        >
          {secondaryAction}
          {onCancel && (
            <Button onClick={onCancel} disabled={submitting}>
              {cancelLabel}
            </Button>
          )}
          <Tooltip title="* fields are required" arrow placement="top">
            <span>
              <Button
                variant="contained"
                onClick={() => onSubmit(buildFormData())}
                disabled={submitDisabled || submitDisabledProp || submitting}
              >
                {submitLabel}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Grid>
    </Grid>
  );
};
